<template>
  <v-container>
    <v-row>
      <v-col>
        <category-banner-image
          v-if="selectedCategory && selectedCategory.contributes_content"
          :categoryId="selectedCategory.id"
        ></category-banner-image>
        <h1 v-else>Broadcasts</h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="dataTableColumn">
        <v-card>
          <v-card-title>
            <h3 style="text-transform: capitalize; word-break: normal;">
              <template v-if="title === 'All'">
                All Broadcasts
              </template>
              <template v-else>
                Category: {{ title }}
              </template>
            </h3>
            <v-tooltip bottom>
              <template v-slot:activator="{on, attrs}">
                <v-btn
                  v-on="on"
                  v-bind="attrs"
                  @click.native="getBroadcasts(); $gtag.clickButton('Refresh ' + title + ' broadcasts');"
                  class="ml-2"
                  :loading="loading"
                  small
                  fab
                >
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Refresh</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{on, attrs}">
                <v-btn
                  v-on="on"
                  v-bind="attrs"
                  @click.native="onClickShare(); $gtag.clickButton('Shared ' + title + ' broadcasts');"
                  class="ml-2"
                  small
                  fab
                >
                  <v-icon>mdi-share-variant</v-icon>
                </v-btn>
              </template>
              <span>Share</span>
            </v-tooltip>
            <v-spacer></v-spacer>
            <v-text-field
              :v-model="search"
              @input="debounceSearch"
              append-icon="mdi-magnify"
              clearable
              label="Search"
              single-line
              hide-details
            ></v-text-field>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-data-table
              :server-items-length="totalBroadcasts"
              :loading="loading"
              :options.sync="options"
              :items="broadcasts"
              :headers="broadcastHeaders"
              :search="search"
              :footer-props="footerProps"
              @update:page="getBroadcasts"
              @update:sortBy="getBroadcasts"
              @update:options="getBroadcasts"
              @click:row="goToStreamPage"
              class="clickable"
            >
              <template v-slot:item.date_formatted="{ item }">
                <template v-if="item.display_date && item.stage === 'vod'">
                  {{ item.date_formatted }}
                </template>
                <template v-if="item.stage === 'scheduled'">
                  <strong>{{ item.weekday }} @ {{ item.start_time_formatted }}</strong>
                  <v-chip small class="ml-2">Scheduled</v-chip>
                </template>
                <template v-if="item.stage === 'live'">
                  <v-chip small color="error" outlined>Live Now</v-chip>
                </template>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import seoNameMixin from '../mixins/seoNameMixin';
import broadcastCategoriesMixin from '../mixins/broadcastCategoriesMixin';
import { mapGetters } from 'vuex';
export default {
  mixins: [seoNameMixin, broadcastCategoriesMixin],
  props: {
    categoryId: {
      type: [String, Number],
      required: false
    },
    categoryName: {
      type: String,
      required: false
    }
  },
  data: () => ({
    broadcasts: [],
    loading: true,
    search: null,
    broadcastHeaders: [
      { text: 'Date', value: 'date_formatted' },
      { text: 'Name', value: 'title' },
      { text: 'Category', value: 'category_name', sortable: false },
    ],
    options: {
      sortBy: [''],
      sortDesc: [true],
      page: 1,
      itemsPerPage: 15,
    },
    footerProps: {
      itemsPerPageOptions: [15, 30, 50, 100],
    },
    totalBroadcasts: 0,
  }),
  computed: {
    ...mapGetters([
      'getCategoryById'
    ]),
    title() {
      if (this.categoryId && this.categoryExists(this.categoryId)) {
        return this.nameByCategoryId(this.categoryId);
      }
      return 'All';
    },
    selectedCategory() {
      if (this.categoryId) {
        return this.getCategoryById(this.categoryId);
      }
      return null;
    }
  },
  methods: {
    debounceSearch(val) {
      this.search = null;
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.search = val;
        this.getBroadcasts();
      }, 300);
    },
    getBroadcasts() {
      this.loading = true;
      axios
        .get('getBroadcasts', {
          params: {
            categoryId: this.categoryId,
            count: this.options.itemsPerPage,
            sort: this.options.sortBy[0],
            descending: this.options.sortDesc[0],
            search: this.search,
            page: this.options.page,
          },
        })
        .then(res => {
          let meta = res.data.meta;
          this.page = meta.current_page;
          this.options.itemsPerPage = meta.per_page;
          this.totalBroadcasts = meta.total;
          this.broadcasts = res.data.data;
          this.loading = false;
        });
    },
    goToStreamPage(broadcast) {
      this.$gtag.clickLink(broadcast.title);
      this.$router.push({ name: 'StreamWithSeoName', params: { id: broadcast.id, seoName: this.seoName(broadcast.title) } })
    },
    updatePageTitle() {
      let name = this.nameByCategoryId(this.categoryId);
      if (name) {
        document.title = `${this.$baseTitle} - ${name}`;
      } else {
        document.title = this.$baseTitle;
      }
    },
    checkCategory() {
      if (this.categoryExists(this.categoryId) && this.categoryId != 43) {
        this.getBroadcasts();
        this.updatePageTitle();
      } else {
        if (this.categoryId) {
          this.categoryNotFoundSnackbar();
          this.$router.push('/broadcasts'); 
        } else {
          this.getBroadcasts();
        }
      }
    },
    categoryNotFoundSnackbar() {
      let snackbar = {
        color: 'error',
        timeout: 3000,
        text: `Category ${this.categoryId} not found`
      };
      this.$store.dispatch('addSnackbar', snackbar);
      this.$gtag.experience('error_category_not_found', this.categoryId);
    },
    onClickShare() {
      this.copyToClipboard(window.location.href);
    },

    copyToClipboard(string) {
      const el = document.createElement('textarea');
      el.value = string;
      document.body.appendChild(el);
      el.select();
      let success = document.execCommand('copy'); // execCommand returns true or false.
      document.body.removeChild(el);

      this.$store.dispatch('addSnackbar', {
        text: success ? `URL copied to clipboard` : 'There was a problem trying to copy that item.',
        color: 'success',
        timeout: 5000,
      });
    },
  },
  watch: {
    categoryId() {
      this.checkCategory();
    },
  },
  created() {
    this.checkCategory();
  },
};
</script>
<style scoped>
  .clickable {
    cursor: pointer;
  }
  .dataTableColumn {
    padding-bottom: 80px;
  }
</style>
